import React, { useState, useEffect } from "react";
import ClearFilters from "./clearFilters.js";
import AreaSelect from "../DataVisualization/GeneralMaps/AreaSelect";
import { MapContainer } from "react-leaflet";

function MapSection(props) {
  const [isSelecting, setIsSelecting] = useState(false);
  const hasCoordinates = props.filters.coordinates.eastLongitude !== "" && 
                         props.filters.coordinates.northLatitude !== "";

  const handleBoundsUpdate = (bounds) => {
    if (!bounds || bounds.east === undefined || bounds.north === undefined) {
      return;
    }

    const coordinates = {
      eastLongitude: parseFloat(bounds.east).toFixed(6),
      northLatitude: parseFloat(bounds.north).toFixed(6),
      southLatitude: parseFloat(bounds.south).toFixed(6),
      westLongitude: parseFloat(bounds.west).toFixed(6),
    };

    props.setParam("northLat", coordinates.northLatitude);
    props.setParam("southLat", coordinates.southLatitude);
    props.setParam("eastLong", coordinates.eastLongitude);
    props.setParam("westLong", coordinates.westLongitude);

    props.setFilters((prevState) => ({
      ...prevState,
      coordinates: coordinates,
    }));
  };

  const handleSelectionComplete = () => {
    setIsSelecting(false);
  };

  const handleClear = (e) => {
    e.preventDefault();
    setIsSelecting(false);

    const coordinates = {
      eastLongitude: "",
      northLatitude: "",
      southLatitude: "",
      westLongitude: "",
    };

    props.removeParam("northLat");
    props.removeParam("southLat");
    props.removeParam("eastLong");
    props.removeParam("westLong");

    props.setFilters((prevState) => ({
      ...prevState,
      coordinates: coordinates,
    }));
  };

  const paramKey = {
    northLatitude: "northLat",
    southLatitude: "southLat",
    eastLongitude: "eastLong",
    westLongitude: "westLong",
  };

  const handleCoordinateChange = (field, value) => {
    props.setFilters((prevState) => ({
      ...prevState,
      coordinates: { ...prevState.coordinates, [field]: value },
    }));
    if (value === "") {
      props.removeParam(paramKey[field]);
    } else {
      props.setParam(paramKey[field], value);
    }
  };

  const handleSelectAreaClick = () => {
    if (!hasCoordinates) {
      setIsSelecting(true);
    } else if (isSelecting) {
      setIsSelecting(false);
    }
  };

  return (
    <>
      <div className="px-2 pb-3">
        <ClearFilters
          filters={props.filters}
          setFilters={props.setFilters}
          showCoordinates={hasCoordinates}
          setShowCoordinates={() => {}}
          removeParam={props.removeParam}
        />
      </div>
      <div className="py-0 max-w-md mx-0 overflow-hidden border-b border-gray-300 px-2 dark:border-gray-700">
        <div className="relative rounded-md overflow-hidden">
          <MapContainer
            center={[69.116864, -105.059681]}
            zoom={2}
            minZoom={1}
            style={{ height: "300px", zIndex: 0 }}
            worldCopyJump={true}
            maxBoundsViscosity={1}
            attributionControl={false}
          >
            <AreaSelect
              isSelecting={isSelecting}
              onBoundsUpdate={handleBoundsUpdate}
              onSelectionComplete={handleSelectionComplete}
              bounds={[
                [
                  props.filters.coordinates.northLatitude || "",
                  props.filters.coordinates.eastLongitude || "",
                ],
                [
                  props.filters.coordinates.southLatitude || "",
                  props.filters.coordinates.westLongitude || "",
                ],
              ]}
            />
          </MapContainer>
          <div className="absolute z-10 right-1 top-2">
            <button
              onClick={handleSelectAreaClick}
              type="button"
              disabled={hasCoordinates && !isSelecting}
              className={`w-full justify-center content-center items-center px-3 py-2 center border text-sm leading-4 font-medium rounded-md shadow-sm ${
                hasCoordinates && !isSelecting
                  ? "border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed"
                  : "border-gray-400 text-black bg-white hover:bg-gray-100 focus:outline-none"
              }`}
            >
              {isSelecting ? "Done Selecting" : "Select Area"}
            </button>
          </div>
        </div>

        <div className="mt-2 w-full text-center">
          <button
            onClick={handleClear}
            type="button"
            className="w-full justify-center text-center inline-flex items-center px-3 py-2.5 border text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          >
            Clear
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 w-full py-4 mt-6 px-1">
          <div className="flex items-end justify-start">
            <p className="bottom-0 mb-0 font-semibold text-sm dark:text-white">Latitude</p>
          </div>
          <div className="flex items-end justify-start">
            <p className="bottom-0 mb-0 font-semibold text-sm dark:text-white">Longitude</p>
          </div>

          <label htmlFor="northLatitude" className="sr-only">North Latitude</label>
          <input
            id="northLatitude"
            className="rounded-md p-2 border border-gray-300 bg-white shadow-sm text-sm dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            type="text"
            placeholder="North"
            value={props.filters.coordinates.northLatitude || ""}
            onChange={(e) => handleCoordinateChange("northLatitude", e.target.value)}
          />

          <label htmlFor="eastLongitude" className="sr-only">East Longitude</label>
          <input
            id="eastLongitude"
            className="rounded-md p-2 border border-gray-300 bg-white shadow-sm text-sm dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            type="text"
            placeholder="East"
            value={props.filters.coordinates.eastLongitude || ""}
            onChange={(e) => handleCoordinateChange("eastLongitude", e.target.value)}
          />

          <label htmlFor="southLatitude" className="sr-only">South Latitude</label>
          <input
            id="southLatitude"
            className="rounded-md p-2 border border-gray-300 bg-white shadow-sm text-sm dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            type="text"
            placeholder="South"
            value={props.filters.coordinates.southLatitude || ""}
            onChange={(e) => handleCoordinateChange("southLatitude", e.target.value)}
          />

          <label htmlFor="westLongitude" className="sr-only">West Longitude</label>
          <input
            id="westLongitude"
            className="rounded-md p-2 border border-gray-300 bg-white shadow-sm text-sm dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
            type="text"
            placeholder="West"
            value={props.filters.coordinates.westLongitude || ""}
            onChange={(e) => handleCoordinateChange("westLongitude", e.target.value)}
          />
        </div>
      </div>
    </>
  );
}

export default MapSection;
